<script setup lang="ts">
import { ref, computed } from "vue";
import SharedAutocomplete from "src/components/shared/SharedAutocomplete.vue";
import { useTicketService } from "src/services/ticket-service";
import { useNotification } from "src/composables/useNotification";
import { AddArticlesToTicketType, ArticleFiltersDTOMatchMode } from "src/api/models";
import type { FetchArticleResponseLite } from "src/api/models";
import { postFetchAllArticles } from "src/api/queries/order-portal-bff";
import { formatCurrency } from "src/utils/currency";
import TicketEditSectionHeader from "./TicketEditSectionHeader.vue";

const props = defineProps<{
  ticketId: string;
}>();

const emit = defineEmits<{
  "article-added": [];
}>();

const { addArticlesToTicket } = useTicketService();
const { showSuccess, showError } = useNotification();

const selectedArticle = ref<FetchArticleResponseLite | null>(null);
const articleFetchFn = (query: string, page: number) =>
  postFetchAllArticles(
    {
      name: query || undefined,
      external_reference: query || undefined,
      description: query || undefined,
      match_mode: ArticleFiltersDTOMatchMode.or,
    },
    { page, limit: 50 },
  ) as Promise<{ data?: FetchArticleResponseLite[] | null; total_pages?: number }>;
const quantity = ref(1);
const isOffer = ref(false);
const customPrice = ref<number | null>(null);
const isSubmitting = ref(false);

const isFormValid = computed(() => !!selectedArticle.value?.id);

function handleArticleSelect(event: { value: FetchArticleResponseLite }) {
  selectedArticle.value = event.value;
}

function handleArticleClear() {
  selectedArticle.value = null;
  customPrice.value = null;
}

const unitPrice = computed(() => {
  if (isOffer.value && customPrice.value !== null) return customPrice.value;
  return selectedArticle.value?.sale_price ?? 0;
});

const totalPrice = computed(() => unitPrice.value * quantity.value);

function resetForm() {
  selectedArticle.value = null;
  quantity.value = 1;
  isOffer.value = false;
  customPrice.value = null;
}

async function handleSubmit() {
  if (!isFormValid.value || !props.ticketId || !selectedArticle.value?.id) return;

  if(!selectedArticle.value.preferred_article_option?.id) {
    return;
  }

  isSubmitting.value = true;
  try {
    await addArticlesToTicket(props.ticketId, [
      {
        article_id: selectedArticle.value.preferred_article_option.id,
        quantity: quantity.value,
        type: isOffer.value ? AddArticlesToTicketType.fixed : AddArticlesToTicketType.variable,
        price: isOffer.value ? customPrice.value : null,
      },
    ]);

    showSuccess({ message: "Article added" });
    emit("article-added");
    resetForm();
  } catch (error) {
    console.error("Error adding article:", error);
    showError({ message: "Failed to add article" });
  } finally {
    isSubmitting.value = false;
  }
}
</script>

<template>
  <aside class="panel">
    <div class="panel-scroll">
      <div class="p-5">
        <TicketEditSectionHeader title="Article" />

        <SharedAutocomplete
          v-model="selectedArticle"
          :fetch-fn="articleFetchFn"
          placeholder="Search article..."
          @item-select="handleArticleSelect"
          @clear="handleArticleClear"
        >
          <template #option="{ option }">
            <div class="ac-option">
              <span v-if="option.preferred_article_option?.external_reference" class="ac-option-sub">{{ option.preferred_article_option?.external_reference }}</span>
              <span class="ac-option-name">{{ option.preferred_article_option?.brand_name }}</span>
              <span class="ac-option-name">{{ option.name }}</span>
              <span v-if="option.sale_price" class="ac-option-price">{{ formatCurrency(option.sale_price) }}</span>
              <span class="ac-option-name">{{ option.preferred_article_option?.location }}</span>

            </div>
          </template>
          <template #detail="{ item }">
            <div v-if="item.name" class="ac-detail-name">{{ item.name }}</div>
            <div v-if="item.preferred_article_option?.code" class="ac-detail-field">
              <span class="ac-detail-label">Code</span>
              <span class="ac-detail-value">{{ item.preferred_article_option?.code }}</span>
            </div>
            <div v-if="item.preferred_article_option?.brand_name" class="ac-detail-field">
              <span class="ac-detail-label">Brand</span>
              <span class="ac-detail-value">{{ item.brand_name }}</span>
            </div>
            <div v-if="item.description" class="ac-detail-field">
              <span class="ac-detail-label">Description</span>
              <span class="ac-detail-value">{{ item.description }}</span>
            </div>
            <div v-if="item.sale_price != null" class="ac-detail-field">
              <span class="ac-detail-label">Sales price</span>
              <span class="ac-detail-value">{{ formatCurrency(item.sale_price) }}</span>
            </div>
            <div v-if="item.preferred_article_option?.purchase_net_price != null" class="ac-detail-field">
              <span class="ac-detail-label">Purchase price</span>
              <span class="ac-detail-value">{{ formatCurrency(item.preferred_article_option?.purchase_net_price) }}</span>
            </div>
            <div v-if="item.measurement" class="ac-detail-field">
              <span class="ac-detail-label">Measurement</span>
              <span class="ac-detail-value">{{ item.measurement }}</span>
            </div>
          </template>
        </SharedAutocomplete>

        <div v-if="selectedArticle?.sale_price" class="article-price-info">
          <span class="article-price-label">Price</span>
          <span class="article-price-val">{{ formatCurrency(selectedArticle.sale_price) }}</span>
        </div>
      </div>

      <div class="divider" />

      <div class="p-5">
        <TicketEditSectionHeader title="Type" />
        <div class="type-row">
          <button class="type-opt" :class="{ 'type-opt-active': !isOffer }" @click="isOffer = false">
            <i class="pi pi-box" style="font-size: 11px" />
            Regular
          </button>
          <button class="type-opt" :class="{ 'type-opt-active': isOffer }" @click="isOffer = true">
            <i class="pi pi-file" style="font-size: 11px" />
            Offer
          </button>
        </div>
      </div>

      <div class="divider" />

      <div class="p-5">
        <TicketEditSectionHeader title="Quantity" />
        <div class="prop-row">
          <span class="prop-label">Amount</span>
          <div class="stepper">
            <button @click="quantity > 1 && quantity--"><i class="pi pi-minus" style="font-size: 9px" /></button>
            <span>{{ quantity }}</span>
            <button @click="quantity++"><i class="pi pi-plus" style="font-size: 9px" /></button>
          </div>
        </div>

        <template v-if="isOffer">
          <div class="prop-row mt-1">
            <span class="prop-label">Custom price</span>
            <div class="price-input-wrap">
              <span class="price-prefix">€</span>
              <input
                type="number"
                v-model.number="customPrice"
                min="0"
                step="0.01"
                class="price-input"
                placeholder="—"
              />
            </div>
          </div>
          <p v-if="selectedArticle?.sale_price" class="hint-text">
            Default: {{ formatCurrency(selectedArticle.sale_price) }}
          </p>
        </template>

        <div v-if="selectedArticle" class="total-row">
          <span class="total-label">Total</span>
          <span class="total-val">{{ formatCurrency(totalPrice) }}</span>
        </div>
      </div>
    </div>

    <div class="panel-footer">
      <div />
      <button class="submit-btn" :disabled="!isFormValid || isSubmitting" @click="handleSubmit">
        <i v-if="isSubmitting" class="pi pi-spinner pi-spin" style="font-size: 12px" />
        {{ isOffer ? "Add to Offer" : "Add Article" }}
      </button>
    </div>
  </aside>
</template>

<style scoped>
.article-price-info {
  display: flex;
  align-items: baseline;
  gap: 0.375rem;
  margin-top: 0.5rem;
  font-size: 0.875rem;
}
.article-price-label {
  color: var(--p-surface-400);
}
.article-price-val {
  color: var(--p-surface-700);
  font-weight: 500;
}
:root[class*="app-dark"] .article-price-val {
  color: var(--p-surface-300);
}

.prop-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.3125rem 0.125rem;
  border-radius: 0.25rem;
}
.prop-label {
  font-size: 0.875rem;
  color: var(--p-surface-500);
}

.price-input-wrap {
  display: inline-flex;
  align-items: center;
  border: 1px solid var(--p-surface-200);
  border-radius: 0.375rem;
  overflow: hidden;
}
:root[class*="app-dark"] .price-input-wrap {
  border-color: var(--p-surface-700);
}
.price-prefix {
  padding: 0.1875rem 0.375rem;
  font-size: 0.8125rem;
  font-weight: 600;
  color: var(--p-surface-400);
  background: var(--p-surface-100);
}
:root[class*="app-dark"] .price-prefix {
  background: var(--p-surface-700);
}
.price-input {
  width: 4.5rem;
  padding: 0.1875rem 0.375rem;
  font-size: 0.875rem;
  font-family: ui-monospace, SFMono-Regular, monospace;
  color: var(--p-surface-700);
  background: transparent;
  border: none;
  outline: none;
  text-align: right;
  -moz-appearance: textfield;
}
:root[class*="app-dark"] .price-input {
  color: var(--p-surface-300);
}
.price-input::-webkit-outer-spin-button,
.price-input::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

.hint-text {
  font-size: 0.8125rem;
  color: var(--p-surface-400);
  margin-top: 0.25rem;
  padding-left: 0.125rem;
}

.total-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 0.75rem;
  padding-top: 0.5rem;
  border-top: 1px solid var(--p-surface-200);
}
:root[class*="app-dark"] .total-row {
  border-top-color: var(--p-surface-700);
}
.total-label {
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--p-surface-500);
}
.total-val {
  font-size: 0.875rem;
  font-weight: 700;
  color: var(--p-green-600);
}
:root[class*="app-dark"] .total-val {
  color: var(--p-green-400);
}
</style>
